import clsx from 'clsx';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  type DragEvent,
  type ClipboardEvent,
  type CSSProperties,
  type FocusEvent as ReactFocusEvent,
} from 'react';
import { useIntl } from 'react-intl';

import { dateTimeInputRoot, expression } from './DateTimeInput.sty.js';
import * as styles from './DateTimeInput.sty.js';
import { useDateTimeInput } from './hooks/useDateTimeInput.js';
import type { DateTimeInputProps, DateTimeInputRef } from './types.js';
import * as visuallyHiddenStyles from '../../core/components/visually-hidden/VisuallyHidden.sty.js';
import { VisuallyHidden } from '../../core/components/visually-hidden/VisuallyHidden.js';
import { useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useDateTimePickerStore } from '../../core/time/timeframeStore/useTimeframeStore.js';
import { validate } from '../../core/time/validation.js';
import { mergeProps as _mergeProps } from '../../core/utils/merge-props.js';
import { Flex } from '../../layouts/flex/Flex.js';
import * as dataTimePickerStyles from '../date-time-picker/DateTimePicker.sty.js';
import { useFormFieldMessagesContext } from '../forms-core/hooks/useFormFieldMessagesContext.js';
import { useFormFieldValidation } from '../forms-core/hooks/useFormFieldValidation.js';
import { FormFieldMessagesAction } from '../forms-core/state/formfield-messages-state-reducer.js';
import { SpinButtonGroup } from '../spin-buttons/spin-button-group/SpinButtonGroup.js';
import { getValidationState } from '../validation/validation-builders.js';

function preventDefault(e: DragEvent | ClipboardEvent) {
  e.preventDefault();
}

/** Date & Time Input used in DateTimeRangePicker and DateTimePicker components
 * @internal
 */
export const DateTimeInput = forwardRef<DateTimeInputRef, DateTimeInputProps>(
  (props, forwardedRef) => {
    const rootRef = useRef<HTMLSpanElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const expressionInputRef = useRef<HTMLSpanElement>(null);

    const min = useDateTimePickerStore((state) => state.min);
    const max = useDateTimePickerStore((state) => state.max);
    const precision = useDateTimePickerStore((state) => state.precision);
    const formatString = useDateTimePickerStore((state) => state.formatString);

    const intl = useIntl();
    const {
      ariaProps: ariaFormProps,
      onChange: validationOnChange,
      onBlur,
      onInvalid,
    } = useFormFieldValidation(inputRef);

    const {
      disabled,
      readOnly,
      placeholder: propPlaceholder,
      className: consumerClassName,
      style: consumerStyle,
      name,
      'data-testid': dataTestId,
      'aria-disabled': ariaDisabledProp,
      controlId,
      form,
      required,
      type = 'datetime',
      inputProps: formControlInputProps,
      validationValueMode,
      onLeadingEdge,
      onTrailingEdge,
      onTrailingOverflow,
    }: DateTimeInputProps = props;

    const dateTimePlaceholder = intl.formatMessage({
      defaultMessage: 'Add date and time',
      id: 'X9SZ3eLw4rz/ODZQ',
      description: 'Default placeholder for the date time input.',
    });
    const datePlaceholder = intl.formatMessage({
      defaultMessage: 'Add date',
      id: 'XC7nO0JIPg0R0kBw',
      description: 'Default placeholder for the date input.',
    });
    const timePlaceholder = intl.formatMessage({
      defaultMessage: 'Add time',
      id: 'MlwM+/jonCtjJ+L7',
      description: 'Default placeholder for the time input.',
    });

    let placeholder = propPlaceholder;
    if (!propPlaceholder) {
      switch (type) {
        case 'datetime':
          placeholder = dateTimePlaceholder;
          break;
        case 'date':
          placeholder = datePlaceholder;
          break;
        case 'time':
          placeholder = timePlaceholder;
          break;
      }
    }

    const ariaLabelingProps = useAriaLabelingProps(props);

    const {
      expressionInputHandler,
      expressionOnFocusHandler,
      expressionOnBlurHandler,
      expressionOnKeyDownHandler,
      onPaste,
      pasteText,
      onPasteUndoKeyDown,
      clearPasteUndo,
      value,
      valueType,
      spinButtons,
      spinButtonHandlers,
      inputValue,
      focus,
      switchToExpressionMode,
      setInputValue,
    } = useDateTimeInput(props, expressionInputRef);

    const isExpressionInput = valueType === 'expression';

    const { dispatch: formMessageDispatch } = useFormFieldMessagesContext();

    useImperativeHandle(
      forwardedRef,
      (): DateTimeInputRef => ({
        element: rootRef.current,
        inputRef: inputRef.current,
        expressionInputRef: expressionInputRef.current,
        spinButtonHandlers,
        focus,
        setExpressionValue: switchToExpressionMode,
        clearPasteUndo,
        pasteText,
        focusLast() {
          if (valueType === 'expression') {
            expressionInputRef.current?.focus();
          } else {
            const handlers = Array.from(spinButtonHandlers.values()).filter(
              (h) => h !== null,
            );
            const lastHandler = handlers[handlers.length - 1];
            lastHandler.element.current?.focus();
            lastHandler.selectAll();
          }
        },
        validate() {
          const textContent = expressionInputRef.current?.textContent;
          // Reset stale inputValue when expression span is empty so the hidden
          // input correctly reports valueMissing for required-field validation.
          if (valueType === 'expression' && !textContent) {
            setInputValue('');
          }
          validate(value ?? null, min, max, {
            formMessageDispatch,
            precision,
            showError: true,
            checkOrder: true,
            ref: rootRef,
            expectedFormat: formatString,
            valueMode: validationValueMode,
            // In single mode, required validation is handled by core time validation
            // (emits native valueMissing). Keeping native `required` here would add a
            // second browser `valueMissing` message with identical text.
            required: validationValueMode === 'single' ? false : required,
          });
          formMessageDispatch?.({
            type: FormFieldMessagesAction.SETISTOUCHED,
            payload: { isTouched: true },
          });
        },
        validateAsync: async () => {
          const textContent = expressionInputRef.current?.textContent;
          if (valueType === 'expression' && !textContent) {
            setInputValue('');
          }
          validate(value ?? null, min, max, {
            formMessageDispatch,
            precision,
            showError: true,
            checkOrder: true,
            ref: rootRef,
            expectedFormat: formatString,
            valueMode: validationValueMode,
            required: validationValueMode === 'single' ? false : required,
          });
          formMessageDispatch?.({
            type: FormFieldMessagesAction.SETISTOUCHED,
            payload: { isTouched: true },
          });
          return getValidationState(inputRef.current, 'manual');
        },
      }),
      [
        spinButtonHandlers,
        focus,
        switchToExpressionMode,
        setInputValue,
        value,
        valueType,
        min,
        max,
        formMessageDispatch,
        precision,
        formatString,
        validationValueMode,
        required,
      ],
    );

    const expressionLabel = intl.formatMessage(
      {
        defaultMessage: '{ariaLabelPrefix} expression',
        id: 'M6eNgatb2wf4fQ3I',
        description: 'Textbox to enter a dql timeframe expression.',
      },
      {
        ariaLabelPrefix: ariaLabelingProps['aria-label'],
      },
    );

    useEffect(() => {
      validationOnChange(); // needs to be in an effect here because the value update should happen before the validation, which validates the input ref value
      // validationOnChange doesn't change and can be excluded
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputValue]);

    return (
      <Flex
        ref={rootRef}
        as="span"
        className={clsx(
          dateTimeInputRoot,
          styles.dateTimeInput,
          consumerClassName,
        )}
        style={consumerStyle}
        data-testid={dataTestId}
        onPaste={onPaste}
        onKeyDown={onPasteUndoKeyDown}
        gap={0}
        data-valuetype={valueType}
      >
        <VisuallyHidden>
          <input
            id={controlId}
            ref={inputRef}
            name={name}
            value={inputValue ?? ''}
            onChange={() => {
              /** noop */
            }}
            onFocus={focus}
            onBlur={onBlur}
            onInvalid={onInvalid}
            disabled={disabled}
            tabIndex={-1}
            required={required}
            readOnly={readOnly}
            aria-disabled={ariaDisabledProp}
            {..._mergeProps(
              formControlInputProps ?? ariaFormProps,
              ariaLabelingProps,
            )}
            form={form}
          />
        </VisuallyHidden>

        <span
          className={clsx(expression, {
            [visuallyHiddenStyles.visuallyHidden]: !isExpressionInput,
          })}
          role="textbox"
          spellCheck={false}
          tabIndex={disabled || !isExpressionInput ? -1 : 0}
          style={
            {
              '--data-placeholder': `"${placeholder}"`,
            } as CSSProperties
          }
          data-valuetype="expression"
          ref={expressionInputRef}
          contentEditable={!disabled && !readOnly ? true : undefined}
          onInput={expressionInputHandler}
          onFocus={(e: ReactFocusEvent<HTMLSpanElement>) => {
            if (e.target.hasAttribute('contenteditable') === false) {
              return;
            }
            expressionOnFocusHandler(e);
          }}
          onBlur={(e: ReactFocusEvent<HTMLSpanElement>) => {
            if (e.target.hasAttribute('contenteditable') === false) {
              return;
            }
            expressionOnBlurHandler(e);
          }}
          onKeyDown={expressionOnKeyDownHandler}
          onPaste={preventDefault}
          onDragOver={preventDefault}
          onDrop={preventDefault}
          aria-label={expressionLabel}
        />
        <SpinButtonGroup
          className={clsx(dataTimePickerStyles.spinButtonGroup, {
            [visuallyHiddenStyles.visuallyHidden]: isExpressionInput,
          })}
          valueOverflow
          onLeadingEdge={onLeadingEdge}
          onTrailingEdge={onTrailingEdge}
          onTrailingOverflow={onTrailingOverflow}
        >
          {spinButtons}
        </SpinButtonGroup>
      </Flex>
    );
  },
);
